/**
 * Liste des devises supportées pour la configuration entreprise
 * Utilisé dans le Select2 (searchable select) de la page EnterpriseConfig
 *
 * @see docs/features/CURRENCY_LIST.md
 */

export interface Currency {
  code: string;        // Code ISO 4217 (3 lettres)
  symbol: string;      // Symbole de la devise
  name: string;        // Nom complet en français
  region?: string;     // Région géographique (optionnel)
}

/**
 * Liste principale des devises (Top 10 + devises courantes)
 * Utilisée par défaut dans le Select2 de la configuration entreprise
 */
export const CURRENCIES: Currency[] = [
  // Devise par défaut
  { code: 'MGA', symbol: 'Ar', name: 'Ariary Malagasy', region: 'Afrique' },

  // Devises principales
  { code: 'EUR', symbol: '€', name: 'Euro', region: 'Europe' },
  { code: 'USD', symbol: '$', name: 'Dollar US', region: 'Amérique' },
  { code: 'GBP', symbol: '£', name: 'Livre Sterling', region: 'Europe' },
  { code: 'CHF', symbol: 'CHF', name: 'Franc Suisse', region: 'Europe' },
  { code: 'CAD', symbol: 'C$', name: 'Dollar Canadien', region: 'Amérique' },
  { code: 'JPY', symbol: '¥', name: 'Yen Japonais', region: 'Asie' },
  { code: 'CNY', symbol: '¥', name: 'Yuan Chinois', region: 'Asie' },
  { code: 'AED', symbol: 'د.إ', name: 'Dirham des Émirats', region: 'Moyen-Orient' },
  { code: 'ZAR', symbol: 'R', name: 'Rand Sud-Africain', region: 'Afrique' },
];

/**
 * Liste étendue avec plus de devises (optionnel)
 * À utiliser si besoin de supporter plus de régions
 */
export const CURRENCIES_EXTENDED: Currency[] = [
  ...CURRENCIES,

  // Afrique (autres)
  { code: 'XOF', symbol: 'CFA', name: 'Franc CFA (BCEAO)', region: 'Afrique' },
  { code: 'XAF', symbol: 'FCFA', name: 'Franc CFA (BEAC)', region: 'Afrique' },
  { code: 'NGN', symbol: '₦', name: 'Naira Nigérian', region: 'Afrique' },
  { code: 'EGP', symbol: '£', name: 'Livre Égyptienne', region: 'Afrique' },
  { code: 'MAD', symbol: 'د.م.', name: 'Dirham Marocain', region: 'Afrique' },
  { code: 'TND', symbol: 'د.ت', name: 'Dinar Tunisien', region: 'Afrique' },
  { code: 'KES', symbol: 'KSh', name: 'Shilling Kenyan', region: 'Afrique' },
  { code: 'GHS', symbol: '₵', name: 'Cedi Ghanéen', region: 'Afrique' },

  // Europe (autres)
  { code: 'NOK', symbol: 'kr', name: 'Couronne Norvégienne', region: 'Europe' },
  { code: 'SEK', symbol: 'kr', name: 'Couronne Suédoise', region: 'Europe' },
  { code: 'DKK', symbol: 'kr', name: 'Couronne Danoise', region: 'Europe' },
  { code: 'PLN', symbol: 'zł', name: 'Zloty Polonais', region: 'Europe' },
  { code: 'CZK', symbol: 'Kč', name: 'Couronne Tchèque', region: 'Europe' },
  { code: 'HUF', symbol: 'Ft', name: 'Forint Hongrois', region: 'Europe' },
  { code: 'RON', symbol: 'lei', name: 'Leu Roumain', region: 'Europe' },

  // Amérique (autres)
  { code: 'BRL', symbol: 'R$', name: 'Real Brésilien', region: 'Amérique' },
  { code: 'MXN', symbol: '$', name: 'Peso Mexicain', region: 'Amérique' },
  { code: 'ARS', symbol: '$', name: 'Peso Argentin', region: 'Amérique' },
  { code: 'CLP', symbol: '$', name: 'Peso Chilien', region: 'Amérique' },
  { code: 'COP', symbol: '$', name: 'Peso Colombien', region: 'Amérique' },

  // Asie (autres)
  { code: 'INR', symbol: '₹', name: 'Roupie Indienne', region: 'Asie' },
  { code: 'KRW', symbol: '₩', name: 'Won Sud-Coréen', region: 'Asie' },
  { code: 'SGD', symbol: 'S$', name: 'Dollar Singapourien', region: 'Asie' },
  { code: 'HKD', symbol: 'HK$', name: 'Dollar de Hong Kong', region: 'Asie' },
  { code: 'THB', symbol: '฿', name: 'Baht Thaïlandais', region: 'Asie' },
  { code: 'MYR', symbol: 'RM', name: 'Ringgit Malaisien', region: 'Asie' },
  { code: 'IDR', symbol: 'Rp', name: 'Roupie Indonésienne', region: 'Asie' },
  { code: 'PHP', symbol: '₱', name: 'Peso Philippin', region: 'Asie' },
  { code: 'VND', symbol: '₫', name: 'Dong Vietnamien', region: 'Asie' },

  // Moyen-Orient (autres)
  { code: 'SAR', symbol: '﷼', name: 'Riyal Saoudien', region: 'Moyen-Orient' },
  { code: 'QAR', symbol: '﷼', name: 'Riyal Qatari', region: 'Moyen-Orient' },
  { code: 'ILS', symbol: '₪', name: 'Shekel Israélien', region: 'Moyen-Orient' },
  { code: 'TRY', symbol: '₺', name: 'Livre Turque', region: 'Moyen-Orient' },

  // Océanie
  { code: 'AUD', symbol: 'A$', name: 'Dollar Australien', region: 'Océanie' },
  { code: 'NZD', symbol: 'NZ$', name: 'Dollar Néo-Zélandais', region: 'Océanie' },
];

/**
 * Trouver une devise par son code
 */
export function getCurrencyByCode(code: string): Currency | undefined {
  return CURRENCIES_EXTENDED.find(c => c.code.toLowerCase() === code.toLowerCase());
}

/**
 * Obtenir le symbole d'une devise par son code
 */
export function getCurrencySymbol(code: string): string {
  const currency = getCurrencyByCode(code);
  return currency?.symbol || code;
}

/**
 * Obtenir le nom complet d'une devise par son code
 * Utilisé pour "Arrêté à X [NOM_DEVISE]"
 */
export function getCurrencyName(code: string): string {
  const currency = getCurrencyByCode(code);
  return currency?.name || code.toUpperCase();
}

/**
 * Filtrer les devises selon une recherche
 */
export function filterCurrencies(
  search: string,
  currencies: Currency[] = CURRENCIES
): Currency[] {
  const lowerSearch = search.toLowerCase().trim();

  if (!lowerSearch) {
    return currencies;
  }

  return currencies.filter(curr =>
    curr.code.toLowerCase().includes(lowerSearch) ||
    curr.name.toLowerCase().includes(lowerSearch) ||
    curr.symbol.includes(search) ||
    curr.region?.toLowerCase().includes(lowerSearch)
  );
}
